<template>
  <div class="profile">
    <h2 class="profile__heading">
      {{ $t('profile.heading') }}
    </h2>
    <div class="profile__container">
      <div class="profile__item">
        <div class="">
          {{ $t('profile.name') }}
        </div>
        <div>
          {{ userStore.user?.name }} {{ userStore.user?.surname }}
        </div>
      </div>
      <div v-if="lastWin" class="profile__item">
        <div class="">
          {{ $t('profile.street') }}
        </div>
        <div>
          {{ lastWin.street }}
        </div>
      </div>
      <div v-if="lastWin" class="profile__item">
        <div class="">
          {{ $t('profile.city') }}
        </div>
        <div>
          {{ lastWin.city }}
        </div>
      </div>
      <button class="profile__item profile__item--action" @click="openPscModal">
        <div class="">
          {{ $t('profile.psc') }}
        </div>
        <div class="flex items-center gap-0.5">
          <div>
            {{ psc }}
          </div>
          <div>
            <DefaultIconsChevronDown/>
          </div>
        </div>
      </button>
      <NuxtLink :to="supportUrl" external target="_blank" class="profile__item profile__item--action">
        <div class="">
          {{ $t('profile.btn-support') }}
        </div>
        <div>
          <DefaultIconsChevronDown/>
        </div>
      </NuxtLink>
      <button class="profile__item profile__item--action" @click="logout">
        <div class="text-primary-500">
          {{ $t('profile.btn-sign-out') }}
        </div>
        <div class="text-primary-500">
          <DefaultIconsChevronDown/>
        </div>
      </button>
    </div>
    <div v-if="userTotalPrize" class="mt-10">
      <h3>
        {{ $t('profile.heading-winnings') }}
      </h3>
      <div class="box px-6 py-2.5 text-xs mt-3">
        <div class="text-white/80">
          {{ $t('profile.heading-winnings-summary') }}
        </div>
        <div class="font-bold text-base text-highlighted">
          {{ userTotalPrize }}
        </div>
      </div>
    </div>
    <DefaultFaqs />
    <!-- Modals -->
    <DefaultModalsPscModal
      v-if="pscModalOpen"
      :isOpen="pscModalOpen"
      :is-loading="userStore.isLoading"
      :step="pscModalStep"
      :psc="userStore.user?.psc"
      :age-consent="userStore.user?.hasAgeConsent"
      :error-message="pscErrorMessage"
      @close="closePscModal"
      @submit="submitPsc"
    />
  </div>
</template>
<script setup lang="ts">
import {ref} from "vue";
import {PscModalStep} from "~/libs/constants/PscModalStep";
import {useI18n} from "vue-i18n";
import {useGameDrawWinnersStore} from "~/stores/useGameDrawWinnersStore";
import { iframeAuthRef } from '~/authBridge'
import {pushToDataLayer} from "~/composables/useAnalytics";

const {t} = useI18n()
const logger = useFrontendLogger()
const userStore = useUserStore()
const gameDrawWinnersStore = useGameDrawWinnersStore('profile')
const gameStore = useGamesStore('stats')
const currency = useCurrency()
const config = useRuntimeConfig()
const supportUrl = config?.public?.contactFormUrl

const emits = defineEmits(['logout'])

const lastWin = computed(() => {
  return gameDrawWinnersStore.getLastWinner()
})

const pscModalStep = ref<PscModalStep>(PscModalStep.FORM)

const psc = computed(() => {
  const psc = userStore.user?.psc || t('profile.empty-psc')
  return `${psc.substring(0, 3)} ${psc.substring(3, 5)}`
})

const pscModalOpen = ref<boolean>(false)
const pscErrorMessage = ref<string | undefined>(undefined)

const openPscModal = () => {
  // --- ANALYTIKA: Otevření modálu ---
  pushToDataLayer({
    "event": "clickTo_content",
    "clickTo": "psc",
    "link": {
      "text": "Vyplnit PSČ a začít hrát"
    }
  });
  // ----------------------------------

  pscModalOpen.value = true
  pscModalStep.value = PscModalStep.FORM
}

const closePscModal = () => {
  pscModalOpen.value = false
}

const submitPsc = async (payload: any) => {
  pscErrorMessage.value = undefined
  try {
    await userStore.updateMe({
      psc: payload.psc,
      hasAgeConsent: payload.ageConsent,
    })
    pscModalStep.value = PscModalStep.SAVED
  } catch (e: any) {
    logger.error(e)
    pscErrorMessage.value = t(e.error?.details?.key || 'modal.psc.error-update')
  }
}

const fetchStatistics = async () => {
  try {
    await gameStore.fetchStatistics()
  } catch (e) {
    logger.error(e)
  }
}

const userTotalPrize = computed(() => {
  if (gameStore.stats?.userTotalPrize && gameStore.stats?.userTotalPrize > 0) {
    return currency.toCurrency(gameStore.stats?.userTotalPrize)
  }
  return null
})


const logout = async () => {
  const parentFn = iframeAuthRef.value?.notifyParentLogout;

  if (typeof parentFn === 'function') {
    // iframe má parent → pošleme mu logout info
    parentFn();
  } else {
    // standalone mód
    // uděláme normální lokální logout
    await userStore.logout();
  }

  emits('logout');
}

onMounted(async () => {
  try {
    await gameDrawWinnersStore.fetchUserLastWins()
    await fetchStatistics()
  } catch (e) {}
})

</script>
